import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, setSeo, track } from "@/lib/api";

const P = (...items) => items;

export const PAGES = {
  "/about": {
    eyebrow: "About",
    title: "About Human Home Living",
    lede:
      "Human Home Living is a homeowner decision platform. We help people make better choices about the significant, disruptive, expensive things they add to their homes.",
    sections: [
      {
        h: "What we are",
        p: P(
          "Human Home Living publishes decision guides, comparisons and interactive planning tools for four categories: wellness and recovery, golf simulators, outdoor living, and home power and technology.",
          "These are the decisions where homeowners get stuck — not because they lack product options, but because they cannot tell whether a product will fit their room, work with their electrical service, survive their climate or justify the price step above the alternative.",
          "We exist to resolve that specific kind of stuck."
        ),
      },
      {
        h: "What we are not",
        p: P(
          "We are not a storefront, a coupon site, a deals blog or a mass-produced review farm. We do not rank products we have not researched. We do not publish a page simply because a keyword exists.",
          "We do not manufacture urgency. There is no countdown, no 'limited time', no 'don't miss out'. The visitor decides, on their own timeline."
        ),
      },
      {
        h: "How we are funded",
        p: P(
          "Human Home Living may earn commissions through affiliate partnerships with retailers and manufacturers. If you buy through one of those links, the commission is paid by the retailer and costs you nothing extra.",
          "These relationships do not determine which products we consider appropriate, or the conclusions in our guidance."
        ),
      },
      {
        h: "Our approach",
        p: P(
          "We focus on the questions homeowners ask before significant purchases, explain the important trade-offs clearly, and make it easy to explore relevant options when you're ready.",
          "If you already know what you want, you should be able to get to relevant options quickly. If you don't, our guides and planning tools can help you make a more informed decision."
        ),
      },
    ],
  },

  "/how-we-help": {
    eyebrow: "Our method",
    title: "How We Help Homeowners Choose",
    lede:
      "We start with the homeowner's question, not the product. Human Home Living helps readers understand fit, cost, installation requirements and meaningful product differences — and then get to relevant options quickly.",
    sections: [
      {
        h: "We help you get to useful choices quickly",
        p: P(
          "If you already know what you want, you shouldn't have to complete a questionnaire, use a planning tool or read a long guide before seeing relevant products.",
          "When products match what you're looking for, Human Home Living aims to show the relevant choices available to us rather than limiting you to a small hand-picked selection.",
          "We may also show other options you might want to consider, with the choice to explore more if you want to. Those suggestions are there to broaden your options, not steer you away from what you asked for.",
          "If you're unsure what you need, our guides and planning tools are available to help. They're optional, not gates to shopping."
        ),
      },
      {
        h: "How we evaluate options",
        p: P(
          "A product can be excellent and still be the wrong choice for your home. A sauna may fit your budget but require electrical work you weren't planning for. A golf simulator may have the features you want but need more room than you have. A massage chair may look perfect online but be too large for the space where you want to use it.",
          "That's why we look beyond ratings and popularity. We consider things like fit, dimensions, electrical requirements, installation, features, ongoing costs and household needs — the details that determine whether an option makes sense for your home.",
          "Affiliate relationships do not determine whether a product is appropriate or the conclusions in our guidance."
        ),
      },
      {
        h: "Verification and corrections",
        p: P(
          "Prices, dimensions, electrical requirements, warranties and manufacturer specifications change. Factual claims of that type are sourced and dated, and figures we cannot yet support are held back rather than published as plausible guesses.",
          "If something here is wrong, we want to know. Corrections are made on the page, and material corrections update the fact-check date."
        ),
      },
    ],
  },

  "/editorial-standards": {
    eyebrow: "Trust",
    title: "Editorial Standards",
    lede:
      "These are the rules our content has to pass before it publishes. They are deliberately restrictive.",
    sections: [
      {
        h: "What we publish",
        p: P(
          "Every guide exists to answer a real homeowner question and to resolve a specific reason people get stuck — cost, fit, installation, compatibility, sizing, running cost or the value of the step up.",
          "We do not create a page merely because people search for a question. If it can be answered inside a stronger guide without weakening it, it stays inside that guide."
        ),
      },
      {
        h: "What we will not do",
        p: P(
          "We do not invent product testing, prices, specifications, warranties, commission rates or merchant claims. We do not fabricate reviews. We do not use fake urgency.",
          "We do not make universal 'best' recommendations without context, and we do not prioritise a product because it pays more."
        ),
      },
      {
        h: "Verification",
        p: P(
          "Prices, dimensions, electrical requirements, warranties, availability and manufacturer specifications change. Every factual claim of that type is recorded with its source, the claim it supports and the date it was verified.",
          "If a figure is not adequately supported, we do not publish it. We would rather explain what drives a number than print one we cannot stand behind."
        ),
      },
      {
        h: "Corrections",
        p: P(
          "If something here is wrong, we want to know. Corrections are made on the page, and material corrections update the fact-check date."
        ),
      },
    ],
  },

  "/affiliate-disclosure": {
    eyebrow: "Trust",
    title: "Affiliate Disclosure",
    lede:
      "Human Home Living helps homeowners research significant purchases and understand their options. Some links on our website may be affiliate links.",
    sections: [
      {
        h: "What that means",
        p: P(
          "If you make a purchase through an affiliate link, Human Home Living may earn a commission at no additional cost to you. The commission is paid by the retailer or manufacturer and it does not change the price you pay."
        ),
      },
      {
        h: "How it affects our guidance",
        p: P(
          "Affiliate relationships do not determine the conclusions in our guides or whether a product is appropriate for a homeowner.",
          "We aim to provide useful information based on factors such as features, compatibility, installation requirements, budget and other considerations relevant to the purchase."
        ),
      },
      {
        h: "How we identify commercial links",
        p: P(
          "When affiliate links appear, we identify commercial relationships as appropriate so visitors can understand when Human Home Living may receive compensation."
        ),
      },
      {
        h: "Questions",
        p: P(
          "If anything about our commercial relationships is unclear, ask us through the contact page and we will explain it."
        ),
      },
    ],
  },

  "/privacy": {
    eyebrow: "Legal",
    title: "Privacy Policy",
    lede:
      "This policy describes what Human Home Living does with the information you give us and the anonymous usage data we collect.",
    sections: [
      {
        h: "What we collect",
        p: P(
          "Email subscriptions: if you subscribe, we store your email address, the interests you selected and the source of the subscription.",
          "Contact messages: if you use our contact form, we store your name, email address, topic and message so that we can reply to you.",
          "Anonymous usage events: we record page views, planning tool starts and completions, product and partner card views, and commercial module impressions and clicks. These events do not include your name, email or planning tool answers.",
          "Anonymous visit measurement: we store a random identifier for your browser and a random identifier for your current visit in your browser's local storage, and record the domain of the website that referred you (the domain only, never the full address or any search text), any campaign tags in the link you followed, the page you arrived on and a broad device type of desktop, mobile or tablet. These identifiers are anonymous and describe a browser or device rather than a person: a different browser, a different device or clearing your local storage counts as a separate visitor. We do not store IP addresses, precise location, names, email addresses or phone numbers alongside this data."
        ),
      },
      {
        h: "What we do not do",
        p: P(
          "We do not sell personal data. We do not store the answers you enter into planning tools on our servers — tool calculations run in your browser. We do not run third-party advertising on this site.",
          "We do use the Pinterest tag so that Pinterest can measure visits that come from Pinterest. It receives the page address and Pinterest's own cookie identifier. We have deliberately left Pinterest's Enhanced Match turned off, so your email address — including any hashed form of it — is never sent to Pinterest."
        ),
      },
      {
        h: "Email",
        p: P(
          "We use your email address to reply to you or to send the guides, product discoveries and home living inspiration you subscribed for. You will always be able to unsubscribe from any email we send.",
          "Newsletter signups use confirmed opt-in: we record the consent box you ticked, the date and time, the page or form you used and the version of this wording, then email you a confirmation link. Nothing marketing-related is sent until you click it. Your address, interests and consent record are stored in this website's own database and are not sold or shared.",
          "Every marketing email carries a one-click unsubscribe link and a link to update your interests. Unsubscribed addresses are suppressed permanently and are never re-added unless you subscribe again yourself."
        ),
      },
      {
        h: "Contact",
        p: P("Privacy questions can be sent through our contact page."),
      },
    ],
  },

  "/terms": {
    eyebrow: "Legal",
    title: "Terms of Use",
    lede: "The terms that apply when you use Human Home Living.",
    sections: [
      {
        h: "Informational purpose",
        p: P(
          "Everything on Human Home Living is provided for general information and planning purposes. It is not professional advice — electrical, structural, construction, medical or otherwise.",
          "Planning tool outputs are estimates and directional guidance. They are not electrical designs, structural calculations, quotes or product compatibility guarantees."
        ),
      },
      {
        h: "Verify before you build or buy",
        p: P(
          "Before purchasing or installing anything discussed on this site, verify requirements with the manufacturer's current published documentation and with a qualified professional for your jurisdiction. Electrical work in particular must be confirmed by a licensed electrician."
        ),
      },
      {
        h: "Affiliate relationships",
        p: P(
          "Human Home Living may earn commissions through affiliate partnerships. See our affiliate disclosure for how that works and how it affects — and does not affect — our guidance."
        ),
      },
      {
        h: "Content ownership",
        p: P(
          "Content on this site belongs to Human Home Living unless otherwise attributed. Product names and trademarks belong to their respective owners and are used for identification only."
        ),
      },
    ],
  },
};

export default function StaticPage() {
  const { pathname } = useLocation();
  const page = PAGES[pathname];

  useEffect(() => {
    if (page) {
      // "About Human Home Living" already carries the brand: appending it again produced
      // "About Human Home Living — Human Home Living" in search results.
      setSeo({
        title: page.title.includes("Human Home Living")
          ? page.title
          : `${page.title} — Human Home Living`,
        description: page.lede,
      });
      track({ event_type: "page_view", page: pathname });
    }
  }, [pathname, page]);

  if (!page) return null;

  return (
    <div data-testid={`static-page-${pathname.replace("/", "")}`}>
      <header className="bg-white border-b border-line px-5 md:px-8 pt-16 pb-14">
        <div className="max-w-[820px] mx-auto">
          <span className="eyebrow">{page.eyebrow}</span>
          <h1 className="display text-[38px] md:text-[54px] leading-[1.05] mt-5 text-charcoal">
            {page.title}
          </h1>
          <p className="text-[18px] md:text-[22px] font-light leading-[1.5] text-[#4F4F4F] mt-6">
            {page.lede}
          </p>
        </div>
      </header>
      <div className="px-5 md:px-8 py-14 md:py-20">
        <div className="max-w-[820px] mx-auto">
          {page.sections.map((s, i) => (
            <section key={i} className="mb-12">
              <h2 className="display text-[27px] md:text-[34px] leading-tight text-charcoal mb-5">
                {s.h}
              </h2>
              <div className="prose-hhl">
                {s.p.map((t, j) => (
                  <p key={j}>{t}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ContactPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const freshForm = () => ({
    name: "",
    email: "",
    topic: params.get("topic") === "partnership" ? "Affiliate or partnership enquiry" : "General",
    message: "",
  });
  const [form, setForm] = useState(freshForm);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSeo({
      title: "Contact — Human Home Living",
      description: "Get in touch with Human Home Living — editorial corrections, partnership enquiries and reader questions.",
    });
    track({ event_type: "page_view", page: "/contact" });
  }, []);

  // A fresh visit to /contact always starts clean: navigating away and back must never show a
  // previous success or error state. location.key changes on every navigation to this route.
  useEffect(() => {
    setForm(freshForm());
    setSent(false);
    setFailed(false);
    setBusy(false);
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const r = await api.post("/contact", form);
      if (!r.data?.ok) throw new Error("not delivered");
      setSent(true);
    } catch {
      setFailed(true);
      toast.error("We couldn’t send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <header className="bg-white border-b border-line px-5 md:px-8 pt-16 pb-14">
        <div className="max-w-[820px] mx-auto">
          <span className="eyebrow">Contact</span>
          <h1 className="display text-[38px] md:text-[54px] leading-[1.05] mt-5 text-charcoal">
            Get in touch
          </h1>
          <p className="text-[18px] md:text-[22px] font-light leading-[1.5] text-[#4F4F4F] mt-6">
            Editorial corrections, partnership enquiries and reader questions all reach the same place.
          </p>
        </div>
      </header>

      <div className="px-5 md:px-8 py-14 md:py-20">
        <div className="max-w-[820px] mx-auto">
          {sent ? (
            <div data-testid="contact-success" className="border border-line bg-white px-8 py-10">
              <h2 className="display text-[28px] text-charcoal">Your message has been received.</h2>
              <p className="text-[16px] font-light text-[#5A5A5A] mt-3 leading-relaxed">
                Thank you for contacting Human Home Living. We’ll review your message and respond as
                soon as possible.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-line px-6 md:px-10 py-9">
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-[13px] font-light text-[#5A5A5A]">Name</span>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name"
                    className="w-full border border-line px-4 py-3.5 mt-2 text-[15.5px] focus:outline-none focus:border-sage transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="text-[13px] font-light text-[#5A5A5A]">Email</span>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email"
                    className="w-full border border-line px-4 py-3.5 mt-2 text-[15.5px] focus:outline-none focus:border-sage transition-colors"
                  />
                </label>
              </div>
              <label className="block mt-5">
                <span className="text-[13px] font-light text-[#5A5A5A]">Topic</span>
                <select
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  data-testid="contact-topic"
                  className="w-full border border-line px-4 py-3.5 mt-2 text-[15.5px] bg-white focus:outline-none focus:border-sage transition-colors"
                >
                  <option>General</option>
                  <option>Editorial correction</option>
                  <option>Affiliate or partnership enquiry</option>
                  <option>Press</option>
                </select>
              </label>
              <label className="block mt-5">
                <span className="text-[13px] font-light text-[#5A5A5A]">Message</span>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  className="w-full border border-line px-4 py-3.5 mt-2 text-[15.5px] focus:outline-none focus:border-sage transition-colors"
                />
              </label>
              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit"
                className="mt-7 px-8 py-4 bg-charcoal text-white text-[12.5px] tracking-[0.14em] uppercase hover:bg-sage transition-colors duration-300 disabled:opacity-60"
              >
                {busy ? "Sending…" : "Send message"}
              </button>
              {failed && (
                <p
                  data-testid="contact-error"
                  className="text-[15px] text-[#991B1B] mt-5 leading-relaxed"
                >
                  We couldn’t send your message. Please try again.
                </p>
              )}
              <p className="text-[12px] font-light text-[#6E6A63] mt-4">
                We reply by email to the address you provide. We never share it.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
